/**
 * Assessment Detail Page
 * View assessment details and start attempt
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { assessmentsApi, attemptsApi } from '../api/index.js';
import { AssessmentWithQuestions } from '../../types/models';
import { MediaImage } from '../components/MediaImage';

export const AssessmentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState<AssessmentWithQuestions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (id) {
      loadAssessment();
    }
  }, [id]);

  const loadAssessment = async () => {
    try {
      setLoading(true);
      const data = await assessmentsApi.getById(id!);
      // Ensure questions is always an array
      if (data && !data.questions) {
        data.questions = [];
      }
      setAssessment(data);
    } catch (err: any) {
      console.error('Error loading assessment:', err);
      setError(err.message || 'Failed to load assessment');
    } finally {
      setLoading(false);
    }
  };

  const handleStart = async () => {
    if (!id) return;

    try {
      setStarting(true);
      const result = await attemptsApi.start(id);
      navigate(`/assessments/${id}/play/${result.attempt_id}`);
    } catch (err: any) {
      setError(err.message || 'Failed to start assessment');
      setStarting(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading assessment...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  if (!assessment) {
    return <div className="error">Assessment not found</div>;
  }

  return (
    <div className="assessment-detail">
      {assessment.image_url && (
        <div className="assessment-image-header">
          <MediaImage
            src={assessment.image_url}
            alt={assessment.title}
            width={assessment.image_width}
            height={assessment.image_height}
          />
        </div>
      )}
      <h1>{assessment.title}</h1>
      {assessment.description && <p className="description">{assessment.description}</p>}
      {assessment.instructions && (
        <div className="instructions">
          <h3>Instructions</h3>
          <p>{assessment.instructions}</p>
        </div>
      )}

      <div className="assessment-info">
        <div className="info-item">
          <span className="label">Questions:</span>
          <span className="value">{assessment.question_count || (assessment.questions?.length ?? 0)}</span>
        </div>
        {assessment.time_limit_seconds && (
          <div className="info-item">
            <span className="label">Time Limit:</span>
            <span className="value">{Math.floor(assessment.time_limit_seconds / 60)} minutes</span>
          </div>
        )}
        {assessment.passing_score_percent && (
          <div className="info-item">
            <span className="label">Passing Score:</span>
            <span className="value">{assessment.passing_score_percent}%</span>
          </div>
        )}
        {assessment.shuffle_questions && (
          <div className="info-item">
            <span className="label">Questions will be shuffled</span>
          </div>
        )}
      </div>

      <div className="actions">
        <button
          onClick={handleStart}
          disabled={starting || assessment.status !== 'published'}
          className="btn btn-primary btn-large"
        >
          {starting ? 'Starting...' : 'Start Assessment'}
        </button>
      </div>
    </div>
  );
};
